import {OtsDateTime} from "../../components/OtsDateTime";
import {ActivityPeriod, ActivityPeriodReport} from "@ots-frontend/ots-data";
import {ArrowDownTrayIcon} from "@heroicons/react/20/solid";
import {useMemo} from "react";
import {OtsActionButton} from "../../components/OtsButton";
import {
  OtsDataTable,
  OtsDataTableBody,
  OtsDataTableCell,
  OtsDataTableHead,
  OtsDataTableHeaderCell,
  OtsDataTableRow,
  OtsDataTableRowHeader,
  OtsDataTableSection,
} from "../../components/OtsDataTable";
import {StatusPill} from "../../components/StatusPill";
import {getActivityPeriodStatusTone} from "./ActivityPeriodHeaderBadges";

export type ActivityPeriodReportItem = {
  report: ActivityPeriodReport;
  activityPeriodId: string;
  activityPeriodName?: string;
  tenantCode?: string;
};

export type ActivityPeriodReportsProps = {
  activityPeriod: ActivityPeriod;
  density?: "compact" | "default";
  onDownload?: (item: ActivityPeriodReportItem) => void;
  onBulkDownload?: (items: ActivityPeriodReportItem[]) => void;
};

const number = new Intl.NumberFormat();
const decimal = new Intl.NumberFormat(undefined, {maximumFractionDigits: 2});
const measurement = (value: number, unit: string) => `${decimal.format(value)}${unit ? ` ${unit}` : ""}`;
const formatCode = (value: string) => value
  .replace(/_/g, " ")
  .toLowerCase()
  .replace(/(^|\s)\S/g, character => character.toUpperCase());

/** Loads over quantity; zero loads and quantities read as a dash. */
const LoadsCell = ({loads, qty, uom}: {loads: number; qty: number; uom: string}) => {
  if (!loads && !qty) return <span className="text-[var(--ots-text-muted)]">—</span>;
  return <>
    <span className="block">{loads ? `${number.format(loads)} loads` : "—"}</span>
    <span className="mt-0.5 block text-xs text-[var(--ots-text-muted)]">{qty ? measurement(qty, uom) : "—"}</span>
  </>;
};

const periodName = (period: ActivityPeriod): string | undefined =>
  period.truckCompanyName || period.operatorUnitName || period.operatorName;

/** Includes nested operator activity periods and removes duplicate report records. */
export const collectActivityPeriodReports = (activityPeriod: ActivityPeriod): ActivityPeriodReportItem[] => {
  const collected: ActivityPeriodReportItem[] = [];
  const seen = new Set<string>();
  const visitedPeriods = new Set<string>();
  const visit = (period: ActivityPeriod) => {
    const periodKey = `${period.database ?? activityPeriod.database ?? ""}:${period.id}`;
    if (visitedPeriods.has(periodKey)) return;
    visitedPeriods.add(periodKey);
    for (const report of period.activityPeriodReportList ?? []) {
      const tenantCode = report.database ?? period.database ?? activityPeriod.database;
      const key = `${tenantCode ?? ""}:${report.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      collected.push({
        report,
        activityPeriodId: period.id,
        activityPeriodName: periodName(period),
        tenantCode,
      });
    }
    for (const child of period.activityPeriods ?? []) visit(child);
  };
  visit(activityPeriod);
  return collected;
};

const openReportDownload = (item: ActivityPeriodReportItem) => {
  if (!item.report.url || typeof document === "undefined") return;
  const link = document.createElement("a");
  link.href = item.report.url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.download = "";
  link.hidden = true;
  document.body.appendChild(link);
  link.click();
  link.remove();
};

/** Starts each generated report download from the same user action. Browsers may ask to allow multiple downloads. */
export const downloadActivityPeriodReports = (items: ActivityPeriodReportItem[]) => {
  items.filter(item => Boolean(item.report.url)).forEach(openReportDownload);
};

/** Activity-period report metadata and direct/bulk download actions. */
export const ActivityPeriodReports = ({
  activityPeriod,
  density = "default",
  onDownload,
  onBulkDownload,
}: ActivityPeriodReportsProps) => {
  const items = useMemo(() => collectActivityPeriodReports(activityPeriod), [activityPeriod]);
  const downloadable = items.filter(item => Boolean(item.report.url));
  const bulkDownload = () => (onBulkDownload ?? downloadActivityPeriodReports)(downloadable);

  if (!items.length) {
    return <div className="rounded-md border border-dashed border-[var(--ots-outline)] bg-[var(--ots-background)] px-5 py-10 text-center">
      <p className="text-sm font-semibold text-[var(--ots-secondary)]">No reports are available.</p>
      <p className="mt-1 text-xs text-[var(--ots-text-muted)]">Generated activity-period reports will appear here.</p>
    </div>;
  }

  return <OtsDataTableSection
    title={`Reports (${number.format(items.length)})`}
    description="Generated reports for this activity period and its nested hauler periods."
    actions={downloadable.length > 1 ? <OtsActionButton
      size={density === "compact" ? "compact" : "default"}
      variant="secondary"
      icon={<ArrowDownTrayIcon/>}
      onClick={bulkDownload}>
      Download all ({number.format(downloadable.length)})
    </OtsActionButton> : undefined}>
    <OtsDataTable density={density} aria-label="Activity period reports">
      <OtsDataTableHead><OtsDataTableRow>
        <OtsDataTableHeaderCell>Report</OtsDataTableHeaderCell>
        <OtsDataTableHeaderCell>For</OtsDataTableHeaderCell>
        <OtsDataTableHeaderCell>Status</OtsDataTableHeaderCell>
        <OtsDataTableHeaderCell align="right">Billable</OtsDataTableHeaderCell>
        <OtsDataTableHeaderCell align="right">Total</OtsDataTableHeaderCell>
        <OtsDataTableHeaderCell>Generated</OtsDataTableHeaderCell>
        <OtsDataTableHeaderCell align="right"><span className="sr-only">Download</span></OtsDataTableHeaderCell>
      </OtsDataTableRow></OtsDataTableHead>
      <OtsDataTableBody>{items.map(item => {
        const report = item.report;
        const reportName = report.rateModelName || report.reportTruckCompanyName || "Activity period report";
        const forName = report.reportTruckCompanyName || item.activityPeriodName || report.companyName || "—";
        const forDetails = [report.companyName, item.tenantCode]
          .filter((value, index, values): value is string => Boolean(value) && values.indexOf(value) === index)
          .join(" · ");
        return <OtsDataTableRow key={`${item.tenantCode ?? ""}:${report.id}`}>
          <OtsDataTableRowHeader>
            <span className="block">{reportName}</span>
            {report.rateModelType && <span className="mt-0.5 block text-xs font-normal text-[var(--ots-text-muted)]">{report.rateModelType}</span>}
          </OtsDataTableRowHeader>
          <OtsDataTableCell>
            <span className="block">{forName}</span>
            {forDetails && <span className="mt-0.5 block text-xs text-[var(--ots-text-muted)]">{forDetails}</span>}
          </OtsDataTableCell>
          <OtsDataTableCell><StatusPill
            size="compact"
            tone={getActivityPeriodStatusTone(report.activityPeriodStatus)}>
            {formatCode(report.activityPeriodStatus)}
          </StatusPill></OtsDataTableCell>
          <OtsDataTableCell align="right"><LoadsCell loads={report.billableLoads} qty={report.billableQty} uom={report.billableQtyUom}/></OtsDataTableCell>
          <OtsDataTableCell align="right"><LoadsCell loads={report.totalLoads} qty={report.totalQty} uom={report.totalQtyUom}/></OtsDataTableCell>
          <OtsDataTableCell><OtsDateTime value={report.createDttm} /></OtsDataTableCell>
          <OtsDataTableCell align="right">
            {report.url ? <a
              href={report.url}
              target="_blank"
              rel="noopener noreferrer"
              download
              aria-label={`Download ${reportName}`}
              title="Download report"
              onClick={() => onDownload?.(item)}
              className="inline-flex size-8 items-center justify-center rounded-lg text-[var(--ots-primary)] hover:bg-[var(--ots-primary-container)] focus-visible:outline-2 focus-visible:outline-[var(--ots-primary)]">
              <ArrowDownTrayIcon aria-hidden="true" className="size-[18px]"/>
            </a> : <span className="text-xs text-[var(--ots-text-muted)]">Unavailable</span>}
          </OtsDataTableCell>
        </OtsDataTableRow>;
      })}</OtsDataTableBody>
    </OtsDataTable>
  </OtsDataTableSection>;
};
